import getScoreTypeDetails from 'src/app/shared/utils/score-type-details';

// ---------------------------------------------------------------------------
// Wire enums
// ---------------------------------------------------------------------------

export const REPORT_TYPES = [
  'credit_watch',
  'esg',
  'esg_risk',
  'immediate_risk',
  'general_risk',
  'eb_sim',
  'eb_sim_risk',
  'investment',
  'event_instances',
  'due_diligence_tabular',
  'due_diligence_tabular_portfolio',
  'esgr_due_diligence_tabular',
  'esgr_due_diligence_tabular_portfolio',
] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

export type Schedule = 'daily' | 'weekly' | 'monthly' | 'quarterly';
export type ReportFormat = 'pdf' | 'html';

export const REPORT_LANGUAGES = ['en', 'de', 'fr', 'es', 'it'] as const;
export type ReportLanguage = (typeof REPORT_LANGUAGES)[number];

const REPORT_LANGUAGE_SET: ReadonlySet<string> = new Set(REPORT_LANGUAGES);

/** Report output language from the browser locale; 'en' when nothing matches. */
export function resolveReportLanguage(): ReportLanguage {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const tags = nav?.languages?.length
    ? nav.languages
    : [nav?.language].filter((tag): tag is string => !!tag);
  for (const tag of tags) {
    const primary = tag.split('-')[0].toLowerCase();
    if (REPORT_LANGUAGE_SET.has(primary)) return primary as ReportLanguage;
  }
  return 'en';
}

export interface FrequencyOption {
  value: Schedule;
  label: string;
  hint: string;
}

export const FREQUENCY_OPTIONS: readonly FrequencyOption[] = [
  { value: 'daily', label: 'Daily', hint: 'Daily (8am)' },
  { value: 'weekly', label: 'Weekly', hint: 'Weekly (every Monday)' },
  { value: 'monthly', label: 'Monthly', hint: 'Monthly (1st)' },
  { value: 'quarterly', label: 'Quarterly', hint: 'Quarterly' },
];

const REPORT_TYPE_SET: ReadonlySet<string> = new Set(REPORT_TYPES);

// ---------------------------------------------------------------------------
// Cockpit ↔ wire score mapping
// ---------------------------------------------------------------------------

export const REPORT_SUPPORTED_SCORES: ReadonlySet<string> = new Set([
  'credit_watch',
  'esg',
  'esg_risk',
  'immediate_risk',
  'general_risk',
  'eb_sim',
  'eb_sim_risk',
  'bb',
]);

export function scoreToReportType(score: string): ReportType | undefined {
  const apiType = getScoreTypeDetails(score).apiType;
  if (!apiType) return undefined;
  return REPORT_TYPE_SET.has(apiType) ? (apiType as ReportType) : undefined;
}

// ---------------------------------------------------------------------------
// Dropdown option shapes
// ---------------------------------------------------------------------------

export interface ReportTypeOption {
  value: ReportType;
  label: string;
}

export interface CockpitScoreOption {
  value: string;
  label: string;
}

export const ENTITY_DISALLOWED_REPORT_TYPES: ReadonlySet<ReportType> = new Set([
  'investment',
]);

export const ENTITY_EXTRA_REPORT_TYPES: ReadonlyArray<ReportTypeOption> = [
  { value: 'due_diligence_tabular', label: 'Credit Due Diligence' },
  { value: 'esgr_due_diligence_tabular', label: 'ESG Due Diligence' },
];

export const PORTFOLIO_EXTRA_REPORT_TYPES: ReadonlyArray<ReportTypeOption> = [
  {
    value: 'due_diligence_tabular_portfolio',
    label: 'Credit Due Diligence',
  },
  {
    value: 'esgr_due_diligence_tabular_portfolio',
    label: 'ESG Due Diligence',
  },
];

export const QUERY_REPORT_TYPES: ReadonlyArray<ReportTypeOption> = [
  { value: 'due_diligence_tabular', label: 'Credit Due Diligence' },
];

// ---------------------------------------------------------------------------
// Portfolio reportability rule
// ---------------------------------------------------------------------------

export const PORTFOLIO_MIN_COMPANIES = 6;

export const PORTFOLIO_TOO_FEW_COMPANIES_TOOLTIP =
  'A portfolio must contain at least 6 companies to generate a report.';

/** Count entries in a portfolio's `content` that are companies. Portfolios can
 *  hold non-company entities; reports only cover companies. */
export function countCompaniesInPortfolio(
  content: ReadonlyArray<string> | undefined,
): number {
  return (content ?? []).filter((e) => e.split(':')[0] === 'company').length;
}

export function hasEnoughCompaniesForReport(
  content: ReadonlyArray<string> | undefined,
): boolean {
  return countCompaniesInPortfolio(content) >= PORTFOLIO_MIN_COMPANIES;
}

// ---------------------------------------------------------------------------
// Wire payloads & DTOs
// ---------------------------------------------------------------------------

export interface CompanyPayload {
  kind: 'company';
  companyId: string;
  language?: ReportLanguage;
}

export interface PortfolioPayload {
  kind: 'portfolio';
  portfolioId: string;
  portfolioName: string;
  companyIds: string[];
  language?: ReportLanguage;
}

export interface QueryPayload {
  kind: 'query';
  queryName: string;
  searchQuery: Record<string, unknown>;
  language?: ReportLanguage;
}

export type SubscriptionPayload =
  | CompanyPayload
  | PortfolioPayload
  | QueryPayload;

/** Stamp the browser locale onto a subscription payload, keeping one already set
 *  (an edited subscription must keep the language it was created with). */
export function withReportLanguage<T extends { payload?: SubscriptionPayload }>(
  dto: T,
): T {
  if (!dto.payload) return dto;
  return {
    ...dto,
    payload: {
      ...dto.payload,
      language: dto.payload.language ?? resolveReportLanguage(),
    },
  };
}

export interface ReportSubscription {
  id: string;
  email: string;
  schedule: Schedule;
  reportType: ReportType;
  reportFormat: ReportFormat;
  recipients: string[];
  payload: SubscriptionPayload;
  active: boolean;
  createdOn?: string;
  updatedOn?: string;
}

export interface CreateSubscriptionDto {
  schedule: Schedule;
  reportType: ReportType;
  reportFormat: ReportFormat;
  recipients: string[];
  payload: SubscriptionPayload;
}

export interface UpdateSubscriptionDto {
  schedule?: Schedule;
  reportType?: ReportType;
  reportFormat?: ReportFormat;
  recipients?: string[];
  payload?: SubscriptionPayload;
  active?: boolean;
}
